import React, { useEffect, useState, useMemo } from "react";
import { API, UPLOADS } from "./../../config/api";
export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [downloaded, setDownloaded] = useState({});
  const [concepts, setConcepts] = useState([]);
  // --- NEW STATE FOR DATE FILTERS ---
  const [dateFilters, setDateFilters] = useState({
    fromDate: "",
    toDate: "",
  });

  useEffect(() => {
    fetchAllOrders();
    fetchConcepts();
  }, []);
  const fetchConcepts = async () => {
    try {
      const res = await fetch(API.CONCEPT_LIST);
      const data = await res.json();
      if (data.status) {
        setConcepts(data.data);
      }
    } catch (error) {
      console.log("Concepts fetch error:", error);
    }
  };

  // ✅ HELPER: Get concept names from IDs
  const getConceptNames = (conceptIds) => {
    if (!conceptIds || !Array.isArray(conceptIds));

    return conceptIds
      .map((id) => {
        const concept = concepts.find((c) => c.id == id);
        return concept ? concept.concept_name : null;
      })
      .filter(Boolean)
      .join(", ");
  };

  const fetchAllOrders = async () => {
    try {
      const response = await fetch(API.ALL_ORDERS);
      const result = await response.json();
      if (result.success) {
        setOrders(result.orders);
      }
    } catch (err) {
      console.error("Error fetching orders", err);
    } finally {
      setLoading(false);
    }
  };

  // --- FILTER LOGIC ---
  // --- FILTER LOGIC (Robust Version) ---
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      // 1. Safety check: If no date filters are set, show everything
      if (!dateFilters.fromDate && !dateFilters.toDate) return true;

      // 2. Safety check: If order_date is missing, hide it or handle it
      if (!order.order_date) return false;

      try {
        // 3. Robust Date Parsing
        // We split the date to ensure YYYY-MM-DD format regardless of browser locale
        const dateParts = order.order_date.split("-");
        let formattedOrderDate;

        if (dateParts[0].length === 4) {
          // Format is YYYY-MM-DD
          formattedOrderDate = order.order_date;
        } else {
          // Format is likely DD-MM-YYYY, convert to YYYY-MM-DD
          formattedOrderDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
        }

        const matchesFrom = dateFilters.fromDate
          ? formattedOrderDate >= dateFilters.fromDate
          : true;
        const matchesTo = dateFilters.toDate
          ? formattedOrderDate <= dateFilters.toDate
          : true;

        return matchesFrom && matchesTo;
      } catch (err) {
        console.error("Date filtering error for order:", order.order_no, err);
        return false;
      }
    });
  }, [orders, dateFilters]);

  const updateStatus = async (orderId, newStatus) => {
    const formData = new FormData();
    formData.append("id", orderId);
    formData.append("status", newStatus);

    try {
      const response = await fetch(API.UPDATE_ORDER_STATUS, {
        method: "POST",
        body: formData,
      });
      const result = await response.json();
      if (result.success) {
        setOrders(
          orders.map((o) =>
            o.id === orderId ? { ...o, status: newStatus } : o,
          ),
        );
        if (selectedOrder && selectedOrder.id === orderId) {
          setSelectedOrder({ ...selectedOrder, status: newStatus });
        }
        alert("Status updated successfully!");
      }
    } catch (err) {
      alert("Failed to update status");
    }
  };

  const handleDownloadEmb = async (
    file,
    designCode,
    orderId,
    category = "malti",
  ) => {
    try {
      if (!file) {
        alert("File not available");
        return;
      }

      const url = `${API.DOWNLOAD}?file=${category.toLowerCase()}/emb/${file}`;

      const response = await fetch(url);

      if (!response.ok) {
        alert("File not found");
        return;
      }

      const blob = await response.blob();

      const extension = file.split(".").pop().toLowerCase();

      let downloadName = designCode;

      // force .emb like your AddDesign logic
      if (extension === "txt") {
        downloadName += ".emb";
      } else {
        downloadName += "." + extension;
      }

      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = downloadName;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      setDownloaded((prev) => ({
        ...prev,
        [`${orderId}_${designCode}`]: true,
      }));
    } catch (error) {
      console.error("Download failed:", error);
      alert("Download failed");
    }
  };
  if (loading) return <div className="text-center mt-5">Loading Orders...</div>;

  return (
    <div className="container-fluid px-4 py-5 bg-light min-vh-100">
      <div className="d-flex justify-content-between align-items-center mb-4 no-print">
        <h2 className="machine-title ">Order Management</h2>

        {/* --- DATE SEARCH UI --- */}
        <div className="d-flex gap-3 align-items-center bg-white p-2 rounded shadow-sm border">
          <div className="d-flex align-items-center gap-2">
            <label className="small fw-bold text-muted mb-0">From:</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={dateFilters.fromDate}
              onChange={(e) =>
                setDateFilters({ ...dateFilters, fromDate: e.target.value })
              }
            />
          </div>
          <div className="d-flex align-items-center gap-2">
            <label className="small fw-bold text-muted mb-0">To:</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={dateFilters.toDate}
              onChange={(e) =>
                setDateFilters({ ...dateFilters, toDate: e.target.value })
              }
            />
          </div>
          {(dateFilters.fromDate || dateFilters.toDate) && (
            <button
              className="btn btn-sm btn-link text-danger p-0"
              onClick={() => setDateFilters({ fromDate: "", toDate: "" })}
            >
              Clear
            </button>
          )}
          <span className="badge bg-teal p-2 px-3">
            {filteredOrders.length} Orders Found
          </span>
        </div>
      </div>

      <div className="table-responsive bg-white rounded shadow-sm no-print">
        <table className="table table-hover align-middle mb-0">
          <thead>
            <tr className="bg-light text-muted small">
              <th className="ps-4">ORDER ID</th>
              <th>USER ID</th>
              <th>CUSTOMER</th>
              <th>CITY</th>
              <th>DATE & TIME</th>
              <th>TOTAL_AMOUNT</th>
              <th>STATUS</th>
              <th className="text-end pe-4">ACTION</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length > 0 ? (
              filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td className="ps-4 fw-bold text-teal">{order.order_no}</td>
                  <td className="fw-bold text-dark">{order.user_id}</td>
                  <td>
                    <div className="fw-bold">{order.user_name}</div>
                    <div className="text-muted small">{order.user_phone}</div>
                  </td>

                  <td>
                    <div className="fw-bold">{order.user_city}</div>
                    <div className="text-muted small">{order.user_state}</div>
                  </td>
                  <td>
                    <div className="fw-bold">{order.order_date}</div>
                    <div className="text-muted small">{order.order_time}</div>
                  </td>
                  <td>
                    <div className="text-center fw-bold">
                      ₹{order.total_amount}
                    </div>
                  </td>
                  {/* <td>
                    <span className={`status-pill ${order.status.toLowerCase()}`}>
                      {order.status}
                    </span>
                  </td> */}

                  <td>
                    {order.status?.toLowerCase() === "confirmed" ? (
                      <span className="text-center text-success fs-4">
                        <i className="bi bi-check-circle-fill"></i>
                      </span>
                    ) : order.status?.toLowerCase() === "cancelled" ? (
                      <span className="text-center text-danger fs-4">
                        <i className="bi bi-x-circle-fill"></i>
                      </span>
                    ) : (
                      <span className="text-center text-warning fs-4">
                        <i className="bi bi-clock-history"></i>
                      </span>
                    )}
                  </td>
                  <td className="text-end pe-4">
                    <button
                      className="btn btn-sm btn-outline-teal"
                      onClick={() => setSelectedOrder(order)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center py-5 text-muted">
                  No orders found for the selected date range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ... (Rest of your Modal code remains exactly the same) ... */}
      {selectedOrder && (
        <div
          className="custom-modal-overlay"
          onClick={() => setSelectedOrder(null)}
        >
          <div
            className="custom-modal-card printable-area"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal content as per your existing code */}
            <div className="modal-header-custom no-print">
              <h5 className="mb-0 fw-bold">
                Order Details {selectedOrder.order_no}
              </h5>
            </div>
            <div className="modal-body-custom">
              <div className="row mb-4 border-bottom pb-3">
                <div className="col-md-6">
                  <label>Customer Details</label>
                  <p className="fw-bold">{selectedOrder.user_name}</p>
                  <p className="small text-muted">
                    {selectedOrder.user_email} | {selectedOrder.user_phone}
                  </p>
                </div>
                <div className="col-md-6 text-md-end">
                  <label>Order Date</label>
                  <p>
                    {selectedOrder.order_date} {selectedOrder.order_time}
                  </p>
                </div>
              </div>
              {/* Order Items Table */}
              <div className="table-responsive border rounded">
                <table className="table table-sm">
                  <thead className="bg-light">
                    <tr>
                      <th className="ps-3">Preview</th>
                      <th className="text-center pe-4">Design code</th>
                      <th className="text-center pe-4">Area</th>
                      <th className="text-center pe-4">Stitch</th>
                      <th className="text-center pe-4">Colour</th>
                      <th className="text-center pe-4">Concept</th>{" "}
                      {/* ✅ Fixed header */}
                      <th className="text-center pe-4">Details</th>{" "}
                      {/* ✅ Fixed header */}
                      <th className="text-center">Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.items?.map((item, idx) => (
                      <tr key={idx}>
                        <td className="ps-3">
                          <img
                            src={`${UPLOADS.DESIGN_IMAGE}${item.jpeg_file}`}
                            style={{ width: "50px" }}
                            alt=""
                          />
                        </td>
                        <td>
                          <div className="fw-bold text-center">
                            {item.design_code}
                          </div>
                        </td>
                        <td className="text-center pe-4">{item.area}</td>
                        <td className="text-center pe-4">{item.stitch}</td>
                        <td className="text-center pe-4">{item.color}</td>

                        {/* ✅ FIXED: Show actual concept and details */}
                        <td className="text-center pe-4">
                          {getConceptNames(
                            selectedOrder.items[idx]?.concepts_array ||
                              JSON.parse(selectedOrder.items[idx]?.concepts),
                          )}
                        </td>

                        <td className="text-center pe-4">{item.details}</td>

                        <td className="text-center">
                          {downloaded[
                            `${selectedOrder.id}_${item.design_code}`
                          ] ? (
                            <span className="text-success fw-semibold">
                              <i className="bi bi-check-circle-fill me-1"></i>
                              Downloaded
                            </span>
                          ) : (
                            <button
                              className="btn btn-sm btn-outline-teal mt-1"
                              onClick={() =>
                                handleDownloadEmb(
                                  item.emb_file || item.emb,
                                  item.design_code,
                                  selectedOrder.id,
                                  item.category || "malti",
                                )
                              }
                            >
                              <i className="bi bi-download me-1"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-end mt-3">
                <h5 className="fw-bold">
                  Total: ₹{selectedOrder.total_amount}
                </h5>
              </div>
            </div>
            <div className="modal-footer-custom no-print">
              <button
                className="btn btn-light border"
                onClick={() => setSelectedOrder(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .bg-teal, .btn-teal { background-color: #00a99d !important; color: white !important; }
        .text-teal { color: #00a99d !important; }
        .btn-outline-teal { border: 1px solid #00a99d; color: #00a99d; }
        .status-pill { padding: 4px 12px; border-radius: 50px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
        .confirmed, .completed { background: #e6f4ea; color: #1e7e34; }
        .cancelled { background: #fce8e8; color: #d93025; }
        .pending { background: #fff4e5; color: #664d03; }
       
        .custom-modal-card { background: white; width: 95%; max-width: 850px; border-radius: 12px; overflow-y: auto; max-height: 90vh; }
        .modal-header-custom { padding: 15px 25px; border-bottom: 1px solid #eee; display: flex; justify-content: space-between; background: #f8f9fa; position: sticky; top: 0; z-index: 10; }
        .modal-body-custom { padding: 25px; }
        .modal-body-custom label { font-size: 10px; text-transform: uppercase; color: #888; font-weight: bold; display: block; }
        .modal-footer-custom { padding: 15px 25px; border-top: 1px solid #eee; text-align: right; background: #f8f9fa; }
      `}</style>
    </div>
  );
}
